import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatErr, inr } from "@/lib/api";
import { Clock, Users, Check, X, MapPin } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { toast } from "sonner";
import { format } from "date-fns";
import { useTrip } from "@/context/TripContext";
import { OnRequestPanel } from "@/components/OnRequest";

export default function SafariDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [s, setS] = useState(null);
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotId, setSlotId] = useState("");
  const bookingType = "private"; // all safaris are private-jeep only

  useEffect(() => {
    api.get(`/safaris/${slug}`).then((r) => setS(r.data)).catch((e) => toast.error(formatErr(e)));
  }, [slug]);

  useEffect(() => {
    if (!s || !date || s.booking_mode === "on_request") return;
    const d = format(date, "yyyy-MM-dd");
    api.get(`/safaris/${s.id}/availability`, { params: { date: d } })
      .then((r) => { setSlots(r.data); setSlotId(r.data.find((x) => x.remaining_vehicles > 0 || x.remaining_seats > 0)?.id || ""); })
      .catch((e) => toast.error(formatErr(e)));
  }, [s, date]);

  const { setTrip } = useTrip();
  const curSlot = slots.find((x) => x.id === slotId);
  useEffect(() => {
    if (!s) return;
    setTrip({
      safari: s.name,
      date: date ? format(date, "dd MMM yyyy") : "",
      slot: curSlot ? `${curSlot.departure_time} (${curSlot.label})` : "",
      type: bookingType,
    });
  }, [s, date, curSlot, bookingType, setTrip]);

  if (!s) return <div className="jt-container py-20">Loading…</div>;
  const onRequest = s.booking_mode === "on_request";

  const go = () => {
    if (!slotId) return toast.error("Pick a slot first");
    const params = new URLSearchParams({ safari_id: s.id, slot_id: slotId, booking_type: bookingType, vehicles: "1", adults: "2", children: "0" });
    nav(`/checkout?${params.toString()}`);
  };

  return (
    <div>
      <div className="relative h-[55vh] overflow-hidden">
        <img src={s.image_url} alt={s.name} className="w-full h-full object-cover"/>
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"/>
        <div className="absolute inset-x-0 bottom-0 jt-container pb-10 text-white">
          <div className="jt-eyebrow text-[#D4A359] mb-3">{s.category}</div>
          <h1 className="font-display text-4xl md:text-5xl">{s.name}</h1>
          <p className="mt-3 text-[#E5E0D6] max-w-2xl">{s.short_description}</p>
        </div>
      </div>

      <div className="jt-container py-14 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-10">
          <section>
            <h2 className="font-serif text-2xl mb-3 text-[#1E3A2B]">About this safari</h2>
            <p className="text-[#1C2421] leading-relaxed">{s.description}</p>
            <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <Stat icon={Clock} label="Duration" value={`${s.duration_hours} hours`}/>
              <Stat icon={Users} label="Capacity" value={`${s.vehicle_capacity} seats/jeep`}/>
              <Stat icon={MapPin} label="Pickup" value="Hotels in Jawai area"/>
              <Stat icon={Users} label="Pricing" value={s.pricing_mode === "per_vehicle" ? "Per jeep" : "Per seat"}/>
            </div>
          </section>

          {s.gallery?.length > 0 && (
            <section>
              <h2 className="font-serif text-2xl mb-4 text-[#1E3A2B]">Gallery</h2>
              <div className="grid grid-cols-2 gap-3">
                {s.gallery.map((g, i) => <img key={i} src={g} alt="" className="rounded-xl aspect-[4/3] object-cover"/>)}
              </div>
            </section>
          )}

          <section className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="font-serif text-xl mb-3 text-[#1E3A2B]">What&apos;s included</h3>
              <ul className="space-y-2">
                {s.inclusions.map((x, i) => <li key={i} className="flex items-start gap-2 text-sm"><Check size={16} className="text-[#1E3A2B] mt-0.5"/>{x}</li>)}
              </ul>
            </div>
            {s.exclusions?.length > 0 && <div>
              <h3 className="font-serif text-xl mb-3 text-[#1E3A2B]">Not included</h3>
              <ul className="space-y-2">
                {s.exclusions.map((x, i) => <li key={i} className="flex items-start gap-2 text-sm text-[#5C6B63]"><X size={16} className="text-[#C86D51] mt-0.5"/>{x}</li>)}
              </ul>
            </div>}
          </section>

          <section>
            <h3 className="font-serif text-xl mb-2 text-[#1E3A2B]">Cancellation</h3>
            <p className="text-sm text-[#5C6B63]">{s.cancellation_policy}</p>
          </section>

          {s.faqs?.length > 0 && (
            <section>
              <h3 className="font-serif text-xl mb-3 text-[#1E3A2B]">FAQs</h3>
              <Accordion type="single" collapsible>
                {s.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`f-${i}`} data-testid={`detail-faq-${i}`}>
                    <AccordionTrigger className="text-left">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-[#5C6B63]">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>
          )}
        </div>

        <aside className="lg:sticky lg:top-24 self-start">
          <div className="bg-white rounded-2xl ring-1 ring-black/5 p-6 shadow-xl">
            <div className="jt-eyebrow mb-2">from</div>
            <div className="font-serif text-3xl text-[#1E3A2B] mb-5">{inr(s.base_price)}<span className="text-sm text-[#5C6B63] font-sans"> /{s.pricing_mode === "per_vehicle" ? "jeep" : "seat"}</span></div>

            <label className="jt-eyebrow mb-2 block">Date</label>
            <Popover>
              <PopoverTrigger asChild>
                <button className="h-11 w-full rounded-md border border-[#E2DACF] px-3 flex items-center gap-2 text-sm bg-white mb-4" data-testid="detail-date-trigger">
                  {date ? format(date, "dd MMM yyyy") : "Pick date"}
                </button>
              </PopoverTrigger>
              <PopoverContent className="p-0" align="start">
                <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))}/>
              </PopoverContent>
            </Popover>

            <p className="text-xs text-[#5C6B63] mb-4" data-testid="detail-private-note">Private jeep · up to {s.vehicle_capacity} guests</p>

            {date && onRequest && (
              <div className="mb-5"><OnRequestPanel safari={s} date={format(date, "yyyy-MM-dd")} bookingType={bookingType} vehicles={1} adults={2} children={0} testid="detail-request" stack
                title="Arranged on request" text="Pick your date and send a request. Our team will confirm the jeep and timing on WhatsApp."/></div>
            )}
            {date && !onRequest && (
              <>
                <label className="jt-eyebrow mb-2 block">Slot</label>
                <div className="flex flex-col gap-2 mb-5">
                  {slots.length === 0 && <span className="text-sm text-[#5C6B63]">No slots that day.</span>}
                  {slots.length > 0 && slots.every((x) => x.started) && <span className="text-sm text-[#C86D51] font-medium" data-testid="detail-all-started">Today&apos;s safaris have started — no slots left. Please pick another date.</span>}
                  {slots.map((sl) => {
                    const avail = bookingType === "private" ? sl.remaining_vehicles > 0 : sl.remaining_seats > 0;
                    return (
                      <button key={sl.id} disabled={!avail} onClick={() => setSlotId(sl.id)} data-testid={`detail-slot-${sl.id}`}
                        className={`px-3 py-2 rounded-md text-sm text-left border ${slotId === sl.id ? "bg-[#1E3A2B] text-white border-[#1E3A2B]" : "border-[#E2DACF]"} ${!avail && "opacity-40"}`}>
                        {sl.departure_time} · {sl.label} · {inr(sl.price)} {sl.started ? " · safari started" : !avail && " · sold out"}
                      </button>
                    );
                  })}
                </div>
                {!slots.some((x) => (bookingType === "private" ? x.remaining_vehicles : x.remaining_seats) > 0) && !(slots.length > 0 && slots.every((x) => x.started)) && (
                  <div className="mb-5"><OnRequestPanel safari={s} date={format(date, "yyyy-MM-dd")} bookingType={bookingType} vehicles={1} adults={2} children={0} testid="detail-request" stack/></div>
                )}
              </>
            )}

            {!onRequest && <button onClick={go} disabled={!slotId} className="jt-pill-accent w-full justify-center disabled:opacity-40" data-testid="detail-book-btn">Book now</button>}
            {onRequest && !date && <p className="text-sm text-[#5C6B63]" data-testid="detail-request-hint">Pick a date to request this experience.</p>}
            {!onRequest && <Link to="/book" className="block text-center text-xs text-[#5C6B63] mt-3 hover:text-[#1E3A2B]">Need all options? Use full booking engine</Link>}
          </div>
        </aside>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-[#F5F0EB] rounded-xl p-4">
      <Icon size={16} className="text-[#C86D51]"/>
      <div className="text-xs text-[#5C6B63] mt-2">{label}</div>
      <div className="text-sm font-medium">{value}</div>
    </div>
  );
}
